import React, { useEffect, useState } from "react";
import "./vacancyText.css";
import {
  Container,
  Grid,
  Typography,
  useMediaQuery,
  Box,
  IconButton,
  Button,
} from "@mui/material";
import { roleBasedRedirection } from "../../utils/helper";
import { useNavigate, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import DescriptionIcon from "@mui/icons-material/Description";
import { getRequest, putRequest } from "../../api/apiServices";
import {
  getJobVacancyTextByJobId,
  updateJobVacancyTextByJobId,
} from "../../api/apiList";
import Loader from "../../utils/Loading";
import Toast from "../../utils/Toast";
import vacancyTextSchema from "../../validations/createVacancyText";
import * as Yup from "yup";
import TextEditor from "react-quill";
import "react-quill/dist/quill.snow.css";

interface VacancyTextData {
  id: number;
  jobSummariesId: number;
  jobId: number;
  professional: string;
  neutral: string;
  engaging: string;
  createdAt: string;
  updatedAt: string;
}

const VacancyText: React.FC = () => {
  const { id } = useParams();
  const [vacancyText, setVacancyText] = useState<VacancyTextData | null>(null);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:768px)");

  useEffect(() => {
    if (id) {
      fetchData();
    }
  }, [id]);

  const fetchData = async () => {
    setLoading(true);
    const response = await getRequest(getJobVacancyTextByJobId(id));
    if (response) {
      setVacancyText(response?.data?.data);
    }
    setLoading(false);
  };

  const handleUpdateVacancyText = async () => {
    try {
      // Ensure vacancyText is always an object
      const validatedData = {
        professional: vacancyText?.professional || "",
        neutral: vacancyText?.neutral || "",
        engaging: vacancyText?.engaging || "",
      };

      await vacancyTextSchema.validate(validatedData, { abortEarly: false });
      setErrors({});
      setLoading(true);
      const data = {
        professional: vacancyText?.professional,
        neutral: vacancyText?.neutral,
        engaging: vacancyText?.engaging,
      };
      const response = await putRequest(
        updateJobVacancyTextByJobId(id),
        validatedData
      );
      setLoading(false);
      if (response?.data.success) {
        Toast(
          response?.data?.statusMessage ||
            "Job Vacancy Text Updated Successfully"
        );
        navigate(roleBasedRedirection("/jobs"));
      }
    } catch (validationErrors) {
      if (validationErrors instanceof Yup.ValidationError) {
        const newErrors: { [key: string]: string } = {};
        validationErrors.inner.forEach((error) => {
          if (error.path) newErrors[error.path] = error.message;
        });
        setErrors(newErrors);
      }
    }
  };

  const goBack = () => {
    navigate(roleBasedRedirection("/jobs"));
  };

  return loading ? (
    <Loader />
  ) : (
    <Box
      sx={{
        minHeight: "100vh",
        padding: isMobile ? "20px" : "40px 20px",
      }}
    >
      <Container maxWidth="xl">
        {/* Modern Header with Back Button */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            marginBottom: 4,
          }}
        >
          <IconButton
            onClick={goBack}
            sx={{
              width: 48,
              height: 48,
              background: "linear-gradient(135deg, #56c6a9 0%, #84E1CC 100%)",
              color: "#fff",
              boxShadow: "0 4px 12px rgba(132, 225, 204, 0.4)",
              "&:hover": {
                background: "linear-gradient(135deg, #4ab89a 0%, #6fd4ba 100%)",
                transform: "translateY(-2px)",
                boxShadow: "0 6px 16px rgba(132, 225, 204, 0.5)",
              },
              transition: "all 0.3s ease",
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                fontSize: isMobile ? "24px" : "32px",
                color: "#273848",
                marginBottom: 0.5,
              }}
            >
              Vacancy Text Generator
            </Typography>
            <Typography
              sx={{
                fontSize: isMobile ? "13px" : "14px",
                color: "#64748b",
                lineHeight: 1.6,
                maxWidth: "900px",
              }}
            >
              Generate three tailored versions—Professional & Formal, Neutral &
              Clear, and Engaging & Conversational—for different recruitment
              needs. High-quality, on-brand job descriptions that resonate with
              your target audience.
            </Typography>
          </Box>
        </Box>

        {/* Text Editor Cards */}
        <Grid container spacing={3}>
          {[
            {
              label: "Professional & Formal",
              key: "professional",
              icon: "💼",
              description: "Formal tone for traditional industries",
            },
            {
              label: "Neutral & Clear",
              key: "neutral",
              icon: "📋",
              description: "Balanced approach for general use",
            },
            {
              label: "Engaging & Conversational",
              key: "engaging",
              icon: "💬",
              description: "Friendly tone for modern workplaces",
            },
          ]?.map(({ label, key, icon, description }) => (
            <Grid item xs={12} key={key}>
              <Box
                sx={{
                  background: "#ffffff",
                  borderRadius: "20px",
                  padding: isMobile ? 2.5 : 3.5,
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
                  border: "1px solid rgba(132, 225, 204, 0.2)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    boxShadow: "0 8px 30px rgba(132, 225, 204, 0.2)",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                {/* Card Header */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    marginBottom: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: "12px",
                      background:
                        "linear-gradient(135deg, #56c6a9 0%, #84E1CC 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "24px",
                      flexShrink: 0,
                    }}
                  >
                    {icon}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        fontSize: isMobile ? "18px" : "20px",
                        fontWeight: 700,
                        color: "#273848",
                        marginBottom: 0.5,
                      }}
                    >
                      {label}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "13px",
                        color: "#64748b",
                      }}
                    >
                      {description}
                    </Typography>
                  </Box>
                </Box>

                {/* Text Editor */}
                <Box
                  sx={{
                    "& .ql-container": {
                      borderRadius: "0 0 12px 12px",
                      minHeight: "300px",
                      fontSize: "15px",
                    },
                    "& .ql-toolbar": {
                      borderRadius: "12px 12px 0 0",
                      background: "#f8fafc",
                      borderColor: "#e2e8f0",
                    },
                    "& .ql-editor": {
                      minHeight: "300px",
                    },
                    "& .ql-stroke": {
                      stroke: "#64748b",
                    },
                    "& .ql-fill": {
                      fill: "#64748b",
                    },
                    "& .ql-picker-label": {
                      color: "#64748b",
                    },
                  }}
                >
                  <TextEditor
                    value={
                      vacancyText?.[key as keyof VacancyTextData]?.toString() ||
                      ""
                    }
                    onChange={(value: string) => {
                      setVacancyText((prev) =>
                        prev ? { ...prev, [key]: value } : null
                      );

                      if (errors[key]) {
                        setErrors((prevErrors) => ({
                          ...prevErrors,
                          [key]: "",
                        }));
                      }
                    }}
                    className="text-editors"
                  />
                </Box>

                {/* Error Message */}
                {errors[key] && (
                  <Typography
                    sx={{
                      color: "#ef4444",
                      fontSize: "13px",
                      marginTop: 1.5,
                      fontWeight: 500,
                    }}
                  >
                    {errors[key]}
                  </Typography>
                )}
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Save Button */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: 4,
            paddingBottom: 2,
          }}
        >
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleUpdateVacancyText}
            sx={{
              background: "linear-gradient(135deg, #56c6a9 0%, #84E1CC 100%)",
              color: "#fff",
              padding: "14px 32px",
              borderRadius: "12px",
              fontSize: "16px",
              fontWeight: 700,
              textTransform: "none",
              boxShadow: "0 4px 16px rgba(132, 225, 204, 0.4)",
              minWidth: isMobile ? "100%" : "200px",
              "&:hover": {
                background: "linear-gradient(135deg, #4ab89a 0%, #6fd4ba 100%)",
                transform: "translateY(-2px)",
                boxShadow: "0 6px 20px rgba(132, 225, 204, 0.5)",
              },
              transition: "all 0.3s ease",
            }}
          >
            Update & Save
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default VacancyText;
